import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useDispatch, useSelector } from "../redux/hooks.js";
import { setFocusedView } from "../redux/slices/logicViewSlice.js";
import { useQueryParams } from "../route/routes.js";
import {
  DEFAULT_VIEW,
  EditViewType,
  parseProjectViews,
  serializeProjectViews,
} from "../util/projectViewTypes.js";

import { useOnValueChange } from "./useOnValueChange.js";

interface ProjectViewsReturn {
  openViews: EditViewType[];
  focusedView: EditViewType | null;
  inNotebookView: boolean;
  inGraphView: boolean;
  inAppView: boolean;
  inRunStatsView: boolean;
  hasNotebookViewOpen: boolean;
  hasGraphViewOpen: boolean;
  hasAppViewOpen: boolean;
  hasRunStatsViewOpen: boolean;
  changeView: (
    replace: boolean,
    thisView: EditViewType,
    extraParams?: Record<string, string>,
  ) => void;
}

/**
 * Views that cannot be open at the same time. When opening a view,
 * any conflicting views are automatically closed.
 */
const MUTUALLY_EXCLUSIVE: Partial<Record<EditViewType, EditViewType[]>> = {
  [EditViewType.RUN_STATS]: [EditViewType.GRAPH],
  [EditViewType.GRAPH]: [EditViewType.RUN_STATS],
};

export function useProjectViews(): ProjectViewsReturn {
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const paramViews = queryParams.get("view") || DEFAULT_VIEW;
  const [openViews, setOpenViews] = useState<EditViewType[]>([]);

  // Toggle view as focused if any clicks inside
  const dispatch = useDispatch();
  const focusedView = useSelector((state) => state.logicView.focusedView);

  useOnValueChange(paramViews, () => {
    if (paramViews != null) {
      const selViews = parseProjectViews(paramViews);
      setOpenViews(selViews);

      const view = selViews[0];
      if (focusedView !== view) {
        dispatch(setFocusedView(view));
      }
    }
  });

  const changeView = useCallback(
    (
      replace: boolean,
      thisView: EditViewType,
      extraParams?: Record<string, string>,
    ) => {
      let newViews = "";

      if (replace) {
        newViews = thisView;
      } else {
        // add
        if (!openViews.includes(thisView)) {
          const conflicts = MUTUALLY_EXCLUSIVE[thisView] ?? [];
          const filtered = openViews.filter((v) => !conflicts.includes(v));
          newViews = serializeProjectViews([...filtered, thisView]);

          // remove
        } else {
          const tempViews = [...openViews];
          const index = tempViews.indexOf(thisView);
          tempViews.splice(index, 1);
          newViews =
            tempViews.length > 0
              ? serializeProjectViews(tempViews)
              : DEFAULT_VIEW;
        }
      }

      queryParams.set("view", newViews);
      const hadPalette = queryParams.get("dialog") === "palette";
      if (hadPalette) {
        queryParams.delete("dialog");
      }
      if (extraParams) {
        for (const [key, value] of Object.entries(extraParams)) {
          queryParams.set(key, value);
        }
      }
      navigate(
        { search: queryParams.toString() },
        hadPalette ? { replace: true } : undefined,
      );
    },
    [queryParams, navigate, openViews],
  );

  const state = useMemo(() => {
    return {
      openViews,
      focusedView,
      changeView,
      inNotebookView: focusedView === EditViewType.NOTEBOOK,
      inGraphView: focusedView === EditViewType.GRAPH,
      inAppView: focusedView === EditViewType.APP,
      inRunStatsView: focusedView === EditViewType.RUN_STATS,
      hasNotebookViewOpen: openViews.includes(EditViewType.NOTEBOOK),
      hasGraphViewOpen: openViews.includes(EditViewType.GRAPH),
      hasAppViewOpen: openViews.includes(EditViewType.APP),
      hasRunStatsViewOpen: openViews.includes(EditViewType.RUN_STATS),
    };
  }, [changeView, focusedView, openViews]);

  return state;
}
